import { useMemo, useState } from "react";
import ValidationErrors from "../ValidationErrors/ValidationErrors";
import classes from "./SimpleInput.module.css";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import validate from "../../../utils/validations/validateV2";
import checkFieldDisabledStatus from "../../../utils/accessUtils/checkFieldDisabledStatus";
import extractFieldValidations from "../../../utils/accessUtils/extractFieldValidations";
import toEnglishNumbers from "../../../utils/textUtils/toEnglishNumbers";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  pStringDecoder,
  pStringEncoder,
  removeEncodeDecodeParts,
} from "../../../utils/textUtils/pEncodeDecode";

const SimpleInput = ({
  variant = "bottomBorder",
  validityName,
  disabled,
  processualDisabled,
  pageIsProcessual,
  entityIsProcessual,
  skipStateCanEditThisFieldCheck,
  value,
  validateHandler,
  label,
  showPasswordIcon,
  name,
  type,
  maxLength,
  uniqueLabelId,
  onKeyDown,
  ltrInput,
  fieldAccesses,
  fieldNames = [],
  fieldName,
  helperTxt,
  localValidationInfos = {},
  currentState,
  labelIcon,
  valueStyleType,
  forcePe,
  forceEn,
  fieldAddress,
  confirmActionOrder,
  onChange,
  onBlur,
  onFocus,
  skipFieldAccessCheck,
  disableFunctionality,
  tooltipContent,
  notif,
  encodeDecodeStrAllowed = false,
  validationOnFront = false,
  isMongoField,
  hasSQLEntity,
  mongoFieldPath,
  withStar = true,
}) => {
  const { disabled: fieldIsDisabled, processualDisabled: fieldIsProcessualDisabled } = useMemo(
    () =>
      checkFieldDisabledStatus({
        fieldAccesses,
        fieldName,
        pageIsProcessual,
        disabledFromParent: disabled,
        processualDisabledFromParent: processualDisabled,
        currentState,
        entityIsProcessual,
        skipStateCanEditThisFieldCheck,
        isMongoField,
        hasSQLEntity,
        mongoFieldPath: mongoFieldPath ? mongoFieldPath : fieldAddress ? fieldAddress : null,
      }),
    [fieldAccesses, disabled, processualDisabled, entityIsProcessual ? currentState : undefined],
  );
  const { inWhichStateOrderItBecomesNecessary, validationType, isMandatory } = useMemo(
    () =>
      extractFieldValidations({
        fieldAccesses,
        fieldName,
      }),
    [fieldAccesses],
  );

  const [isFocused, setIsFocused] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const keyDownHandler = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };
  const formatMoneyWithCommas = (value) => {
    return new Intl.NumberFormat().format(value);
  };
  const formatMoney = (value) => {
    const numericValue = value.replace(/[^0-9]/g, "") === "" ? null : value.replace(/[^0-9]/g, "");
    return numericValue;
  };
  const onChangeHandler = (e) => {
    const inputValue = e.target.value;
    if (type === "number") {
      if (/^[0-9]+$/.test(toEnglishNumbers(e.target.value)) || e.target.value === "") {
        const properObj = {
          target: {
            name: e.target.name,
            value: e.target.value === "" ? null : toEnglishNumbers(e.target.value),
          },
        };
        onChange(properObj, fieldAddress);
      }
    } else if (type === "withSeparator") {
      const formattedValue = formatMoney(toEnglishNumbers(inputValue));
      const properObj = { target: { name: e.target.name, value: formattedValue } };
      onChange(properObj, fieldAddress);
    } else if (type === "decimal") {
      const englishValue = toEnglishNumbers(inputValue);
      if (/^\d*(\.\d*)?$/.test(englishValue) || englishValue === "") {
        const properObj = {
          target: {
            name: e.target.name,
            value: englishValue === "" ? null : englishValue,
          },
        };
        onChange(properObj, fieldAddress);
      }
    } else if (type === "textOnly") {
      let value = e.target.value || null;
      if (value) {
        value = toEnglishNumbers(value).replace(/[0-9]/g, "");
        if (encodeDecodeStrAllowed) {
          value = pStringEncoder(value);
        } else {
          value = removeEncodeDecodeParts(value);
        }
      }
      const properObj = {
        target: {
          name: e.target.name,
          value,
        },
      };
      onChange(properObj, fieldAddress);
    } else {
      let value = toEnglishNumbers(e.target.value) || null;
      if (value) {
        if (encodeDecodeStrAllowed) {
          value = pStringEncoder(value);
        } else {
          value = removeEncodeDecodeParts(value);
        }
      }
      const properObj = {
        target: { name: e.target.name, value },
      };
      onChange(properObj, fieldAddress);
    }
  };
  const blurHandler = (e) => {
    if (onBlur) {
      onBlur(e, fieldAddress);
    }
    if (validateHandler) {
      const validateOptions = { ...localValidationInfos };
      if (!validationOnFront) {
        if (isMandatory) {
          validateOptions.isOptional = false;
        } else {
          validateOptions.isOptional = true;
        }
        if (!skipStateCanEditThisFieldCheck) {
          if (validationType) validateOptions.type = validationType;
          if (inWhichStateOrderItBecomesNecessary)
            validateOptions.inWhichStateOrderItBecomesNecessary = inWhichStateOrderItBecomesNecessary;
          if (confirmActionOrder) validateOptions.clickedActionOrder = confirmActionOrder;
          if (forceEn) validateOptions.forceLtr = forceEn;
        }
      }
      validateOptions.data = e.target.value;

      const newValue = validate(validateOptions);
      validateHandler(newValue, fieldAddress);
    }
    setIsFocused(false);
  };
  const focusHandler = (e) => {
    if (onFocus) onFocus(e, fieldAddress);
    setIsFocused(true);
  };
  const readOnly = skipFieldAccessCheck ? disabled : fieldIsDisabled || fieldIsProcessualDisabled;
  return (
    <div className={classes.container}>
      <label
        className={[
          "inputPLabel",
          variant === "bottomBorder" ? classes.label : classes.allBorderLabel,
          variant
            ? variant === "bottomBorder"
              ? classes.noMargin
              : classes.marginStart
            : classes.marginStart,
          isFocused ||
          (!isFocused && typeof value === "string" && value?.trim() !== "") ||
          (!isFocused && typeof value === "number" && value !== null)
            ? variant === "bottomBorder"
              ? [classes.labelUp, "pLabelUp"].join(" ")
              : [classes.allBorderLabelUp, "pLabelUp"].join(" ")
            : [classes.labelDown, "pLabelDown"].join(" "),
          readOnly
            ? "disabledinputPLabel"
            : isFocused
              ? variant === "bottomBorder"
                ? "focusedinputPLabel"
                : classes.pinkColor
              : validityName
                ? validityName?.isValid
                  ? "enabledinputPLabel"
                  : "invalidinputPLabel"
                : "enabledinputPLabel",
        ].join(" ")}
        htmlFor={uniqueLabelId ? uniqueLabelId : name}
      >
        <span className={classes.iconContainer}> {labelIcon || null}</span>
        <span className={classes.txtLabel}> {label}</span>
        {withStar ? (
          isMandatory || localValidationInfos?.type === "mandatory" ? (
            <span className={classes.mandatorySign}>*</span>
          ) : null
        ) : null}

        {notif === true ? <ErrorOutlineOutlinedIcon className={classes.notif} /> : null}
      </label>
      {tooltipContent ? <span className={classes.tooltip}>{tooltipContent}</span> : null}
      <input
        autoComplete="off"
        id={uniqueLabelId ? uniqueLabelId : name}
        onFocus={focusHandler}
        maxLength={maxLength}
        onWheel={type === "number" ? (e) => e.target.blur() : null}
        type={type === "decimal" ? "number" : type === "password" && !showPassword ? "password" : "text"}
        name={name}
        inputMode={type === "number" || type === "withSeparator" ? "numeric" : null}
        pattern={type === "textOnly" ? "[A-Za-zs]+" : null}
        disabled={readOnly || disableFunctionality}
        className={[
          "pInput",
          variant === "bottomBorder" ? classes.addFormInput : classes.addFormInputAllBorder,
          ltrInput || type === "number" || type === "withSeparator" ? classes.ltrInput : null,
          readOnly
            ? "disabledPInput"
            : isFocused
              ? variant === "bottomBorder"
                ? "focusedPInput"
                : classes.pinkBorder
              : validityName
                ? validityName?.isValid
                  ? "enabledPInput"
                  : "invalidPInput"
                : variant === "bottomBorder"
                  ? "enabledPInput"
                  : classes.allBorders,
          ,
          valueStyleType === "largeCentered" ? classes.largeCenteredInput : null,
          forcePe ? classes.forcePe : null,
        ].join(" ")}
        value={
          type === "withSeparator"
            ? value === "" || value === null || value === undefined
              ? ""
              : formatMoneyWithCommas(value)
            : value === null
              ? ""
              : encodeDecodeStrAllowed
                ? pStringDecoder(value)
                : value
        }
        onChange={onChangeHandler}
        onKeyDown={keyDownHandler}
        onBlur={blurHandler}
      />
      {showPasswordIcon && value ? (
        <>
          {!showPassword ? (
            <span
              onClick={() => setShowPassword(true)}
              className={variant === "bottomBorder" ? classes.passIcon : classes.allBorderPassIcon}
            >
              <VisibilityOffIcon />
            </span>
          ) : (
            <span
              onClick={() => setShowPassword(false)}
              className={variant === "bottomBorder" ? classes.passIcon : classes.allBorderPassIcon}
            >
              <VisibilityIcon />
            </span>
          )}
        </>
      ) : null}
      {helperTxt ? (
        <span className={["enabledinputPLabel", classes.helperTxtContainer].join(" ")}>
          <span className={classes.helperTxtIcon}>
            <HelpOutlineOutlinedIcon className={classes.questionIcon} />
          </span>
          <span> {helperTxt}</span>
        </span>
      ) : null}
      {validityName ? (
        <ValidationErrors validity={validityName?.isValid} message={validityName?.msg} />
      ) : null}
    </div>
  );
};

export default SimpleInput;
